/**
 * One installed device module's settings, rendered from the module's schema. Fields group by `section`; provisional
 * fields carry a "to confirm with vendor" tag; secrets show "stored" and are only sent when typed. Live video
 * settings sit under a collapsed "Live video (add-on)" heading because streaming is optional.
 */
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CaretDown, CaretRight, PuzzlePiece, VideoCamera } from "@phosphor-icons/react";
import { fieldErrors, http, ModuleInstall, ModuleSchemaField } from "../api";
import { useSession } from "../AuthToken";
import { StatusChip } from "../ui";
import { caps, fonts, tones } from "../theme";
import { Panel, Tag } from "./common";

type Val = string | number | boolean;

const isStream = (f: ModuleSchemaField) => (f.section ? /live|video|stream/i.test(f.section) : /^stream/i.test(f.key));
const isMono = (f: ModuleSchemaField) =>
  ["host", "port", "path", "aet", "number"].includes(f.type) || /fingerprint|aet|path|host|port/i.test(f.key);
const isNumeric = (f: ModuleSchemaField) => f.type === "port" || f.type === "number" || f.type === "integer";

function initial(install: ModuleInstall): Record<string, Val> {
  const out: Record<string, Val> = {};
  for (const f of install.schema) {
    if (f.type === "secret") continue;
    const v = install.settings[f.key];
    // Installs made before the live add-on switch existed have no liveEnabled value; the server treats them as live
    // when a stream address is set. Start from that effective value so saving another field does not turn live off.
    if (f.key === "liveEnabled" && (v === undefined || v === null)) {
      out[f.key] = !!install.settings.streamHost;
      continue;
    }
    out[f.key] =
      v !== undefined && v !== null ? v : (f.default as Val | undefined) ?? (f.type === "boolean" ? false : "");
  }
  return out;
}

export function ModuleSettings({ install, onSaved }: { install: ModuleInstall; onSaved: (m: ModuleInstall) => void }) {
  const { token } = useSession();
  const [values, setValues] = useState<Record<string, Val>>(() => initial(install));
  const [base, setBase] = useState<Record<string, Val>>(() => initial(install));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);

  const sig = JSON.stringify([install.settings, install.secretsSet]);
  useEffect(() => {
    const next = initial(install);
    setBase(next);
    setValues((cur) => (JSON.stringify(cur) === JSON.stringify(base) ? next : cur));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const changedSecrets = Object.entries(secrets).filter(([, v]) => v !== "");
  const dirty = Object.keys(values).some((k) => values[k] !== base[k]) || changedSecrets.length > 0;

  const { groups, stream } = useMemo(() => {
    const g = new Map<string, ModuleSchemaField[]>();
    const s: ModuleSchemaField[] = [];
    for (const f of install.schema) {
      if (isStream(f)) {
        s.push(f);
        continue;
      }
      const k = f.section ?? "";
      g.set(k, [...(g.get(k) ?? []), f]);
    }
    return { groups: [...g.entries()], stream: s };
  }, [install.schema]);

  const required = (f: ModuleSchemaField) => {
    if (!f.required) return "";
    if (f.type === "secret") return install.secretsSet.includes(f.key) || secrets[f.key] ? "" : "Required";
    const v = values[f.key];
    return v === "" || v === undefined ? "Required" : "";
  };
  // Live video is optional: its required fields only block saving once any of them is filled in.
  const streamStarted = stream.some((f) =>
    f.type === "secret"
      ? !!secrets[f.key] || install.secretsSet.includes(f.key)
      : values[f.key] !== "" && values[f.key] !== f.default && values[f.key] !== false
  );
  const missing = install.schema.filter((f) => (isStream(f) ? streamStarted : true) && required(f));

  const save = async () => {
    setBusy(true);
    setErrs({});
    const settings: Record<string, Val> = {};
    for (const f of install.schema) {
      if (f.type === "secret") {
        if (secrets[f.key]) settings[f.key] = secrets[f.key];
        continue;
      }
      const v = values[f.key];
      settings[f.key] = isNumeric(f) && v !== "" ? Number(v) : v;
    }
    const r = await http.patch<ModuleInstall>(token, `/admin/installs/${install.installId}`, { settings });
    setBusy(false);
    if (!r.ok) {
      const fe = fieldErrors(r.error);
      setErrs(fe);
      return setMsg({ tone: "error", text: Object.keys(fe).length ? "Some fields need attention." : r.error });
    }
    setSecrets({});
    const next = initial(r.result);
    setBase(next);
    setValues(next);
    onSaved(r.result);
    setMsg({ tone: "success", text: "Saved. The Pandia applies module settings within about 30 s." });
  };

  const field = (f: ModuleSchemaField) => {
    const label: ReactNode = (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
        {f.label}
        {f.required ? " *" : ""}
        {f.provisional && <Tag>to confirm with vendor</Tag>}
      </Box>
    );
    const err = errs[f.key] || (missing.includes(f) && dirty ? required(f) : "");
    if (f.type === "boolean") {
      return (
        <Grid item xs={12} key={f.key}>
          <FormControlLabel
            control={
              <Switch checked={!!values[f.key]} onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })} />
            }
            label={
              <Box>
                <Typography variant="body2" component="div">
                  {label}
                </Typography>
                {f.help && (
                  <Typography variant="caption" color="text.secondary">
                    {f.help}
                  </Typography>
                )}
              </Box>
            }
            sx={{ alignItems: "flex-start", m: 0 }}
          />
        </Grid>
      );
    }
    if (f.type === "secret") {
      const stored = install.secretsSet.includes(f.key);
      return (
        <Grid item xs={12} sm={6} key={f.key}>
          <TextField
            fullWidth
            size="small"
            type="password"
            autoComplete="new-password"
            label={label}
            value={secrets[f.key] ?? ""}
            placeholder={stored ? "stored" : ""}
            onChange={(e) => setSecrets({ ...secrets, [f.key]: e.target.value })}
            InputLabelProps={{ shrink: stored || !!secrets[f.key] ? true : undefined }}
            error={!!err}
            helperText={
              err ||
              `${f.help ? `${f.help}. ` : ""}${
                stored ? "A value is stored. Type a new value to replace it." : "Stored encrypted, never shown again."
              }`
            }
          />
        </Grid>
      );
    }
    return (
      <Grid item xs={12} sm={f.type === "port" ? 4 : 6} key={f.key}>
        <TextField
          fullWidth
          size="small"
          label={label}
          type={isNumeric(f) ? "number" : "text"}
          select={!!f.options?.length}
          value={String(values[f.key] ?? "")}
          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
          placeholder={f.default !== undefined ? String(f.default) : undefined}
          error={!!err}
          helperText={err || f.help}
          inputProps={{ spellCheck: false }}
          InputProps={isMono(f) ? { sx: { fontFamily: fonts.mono } } : undefined}
        >
          {f.options?.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    );
  };

  return (
    <Panel title={install.name} icon={<PuzzlePiece size={16} />} action={<StatusChip status={install.status} />}>
      <Stack spacing={2.25}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: fonts.mono, mt: -1.5 }}>
          {install.moduleId}
        </Typography>
        {msg && (
          <Alert severity={msg.tone} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        )}
        {groups.map(([section, fields]) => (
          <Box key={section || "main"}>
            {section && (
              <Typography sx={{ ...caps, fontSize: 10, letterSpacing: "0.18em", color: "text.secondary", mb: 1.25 }}>
                {section}
              </Typography>
            )}
            <Grid container spacing={2}>
              {fields.map(field)}
            </Grid>
          </Box>
        ))}
        {!groups.length && !stream.length && (
          <Typography variant="body2" color="text.secondary">
            This module has no settings.
          </Typography>
        )}
        {stream.length > 0 && (
          <Box
            sx={{
              borderRadius: "12px",
              border: 1,
              borderColor: videoOpen ? alpha(tones.live, 0.28) : "divider",
              transition: "border-color 0.3s",
            }}
          >
            <Button
              fullWidth
              color="inherit"
              onClick={() => setVideoOpen((o) => !o)}
              aria-expanded={videoOpen}
              sx={{
                justifyContent: "flex-start",
                borderRadius: "12px",
                px: 2,
                py: 1.25,
                gap: 1,
                color: "text.primary",
              }}
              startIcon={videoOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
            >
              <VideoCamera size={16} />
              <span>Live video (add-on)</span>
              <Box sx={{ flexGrow: 1 }} />
              <Typography
                component="span"
                variant="caption"
                sx={{ textTransform: "none", letterSpacing: 0, color: "text.secondary" }}
              >
                {streamStarted ? "configured" : "optional, not set up"}
              </Typography>
            </Button>
            <Collapse in={videoOpen} unmountOnExit>
              <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Streams the instrument's camera to Jupiter for remote sessions. DICOM worklist and storage work
                  without it.
                </Typography>
                <Grid container spacing={2}>
                  {stream.map(field)}
                </Grid>
              </Box>
            </Collapse>
          </Box>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={save} disabled={!dirty || busy || missing.length > 0}>
            Save module settings
          </Button>
          {dirty && (
            <Button
              color="inherit"
              onClick={() => {
                setValues(base);
                setSecrets({});
                setErrs({});
              }}
            >
              Discard
            </Button>
          )}
        </Stack>
      </Stack>
    </Panel>
  );
}
